import { SUBJECT_KEYS, TSelectedSkuItem } from "@elixir/types";
import React from "react";
import { capitalizeFirstLetter } from "../../../utils/string-utils";
import {
  TSearchPopupProps,
  TSearchSuggestionProps,
} from "../../common/util-components/autosuggest/autosuggest";
import { getAclViewClassName, getServiceIdAndPriceFromServiceDropdownId } from "@elixir/utils";

const SearchSuggestion = ({
  service,
  isHighlighted = false,
  onSelect,
}: Pick<TSearchSuggestionProps, "isHighlighted" | "onSelect"> & {
  service: TSelectedSkuItem;
}) => {
  const { price, service_id, service_name } = service;
  return (
    <div
      className={`flex justify-between items-center text-12 font-400 leading-7 text-neutral-2 hover:bg-darwin-neutral-100 px-16 cursor-pointer ${isHighlighted ? "bg-darwin-neutral-100" : ""
        }`}
      onClick={() => {
        onSelect(service_id as number);
      }}
    >
      <div className="capitalize">{service_name}</div>
      <div className="capitalize">{`Rs. ${price}`}</div>
    </div>
  );
};

export type TBillingServiceSearchPopupProps = TSearchPopupProps & {
  services: TSelectedSkuItem[];
  handleNewCreateService: ({ serviceName }: { serviceName: string }) => void;
};

const BillingServiceSearchPopup = ({
  inputValue,
  options = [],
  services = [],
  highlightedIndex,
  onSuggestSelect,
  handleNewCreateService,
}: TBillingServiceSearchPopupProps) => {
  const handleCreateClick = async () => {
    handleNewCreateService({ serviceName: inputValue || "" });
  };

  const doesServiceWithInputValueAsNameAlreadyExists = React.useMemo(
    () =>
      options.find(
        (option) => option.name.toLowerCase() === inputValue?.toLowerCase()
      ),
    [inputValue, options]
  );

  return (
    <div className="bg-neutral-0 rounded-4 flex flex-col py-8 shadow-900 max-h-240 min-w-360 overflow-hidden">
      <div className="text-10 font-400 leading-5 text-neutral-4 px-16">
        Select an option or create one
      </div>
      {options?.length ? (
        <div className="space-y-12 overflow-y-scroll py-10">
          {options.map((option, index) => {
            const { service_id, price } =
              getServiceIdAndPriceFromServiceDropdownId(`${option.id}`);

            const serviceOption = services.find(
              (s) => s.service_id === service_id && s.price === price
            );
            if (!serviceOption) return null;
            return (
              <SearchSuggestion
                key={`${option.name}_${option.id}_${index}`}
                service={serviceOption}
                isHighlighted={highlightedIndex === index}
                onSelect={() => onSuggestSelect(option.id)}
              />
            );
          })}
        </div>
      ) : null}
      {inputValue && !doesServiceWithInputValueAsNameAlreadyExists ? (
        <div
          className={`text-12 font-700 leading-4 text-darwin-primary-500 px-16 pb-4 cursor-pointer ${getAclViewClassName(SUBJECT_KEYS.SKU_CREATE)}`}
          onClick={handleCreateClick}
        >
          {`Create "${capitalizeFirstLetter(inputValue)}" as a service`}
        </div>
      ) : null}
    </div>
  );
};

export default BillingServiceSearchPopup;
